import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@heidi/design-system/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { RadioGroup, RadioGroupItem } from '@heidi/design-system/radio-group';
import {
  TypographyCaption,
  TypographyH5,
  TypographyP2,
  TypographyP2Bold,
  UserData,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Sprout, X } from 'lucide-react';

import type { SuggestedPatientMatch } from '../suggested-matches';
import { formatDisplayDate } from '../utils/format-display-date';

interface SuggestedMatchReviewContentProps {
  matches: readonly SuggestedPatientMatch[];
  onConfirmMatch: (match: SuggestedPatientMatch) => void;
  onDismiss: () => void;
  className?: string;
  renderDialogLabels?: boolean;
}

interface SuggestedMatchReviewDialogProps extends SuggestedMatchReviewContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SuggestedMatchReviewDialog({
  open,
  onOpenChange,
  ...contentProps
}: SuggestedMatchReviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseIcon
        className="flex max-h-[calc(100vh-4rem)] max-w-2xl flex-col overflow-hidden p-6"
      >
        <SuggestedMatchReviewContent {...contentProps} renderDialogLabels />
      </DialogContent>
    </Dialog>
  );
}

export function SuggestedMatchReviewContent({
  matches,
  onConfirmMatch,
  onDismiss,
  className,
  renderDialogLabels = false,
}: SuggestedMatchReviewContentProps) {
  const intl = useIntl();
  const [selectedMatchId, setSelectedMatchId] = React.useState<string | undefined>(
    matches[0]?.id
  );

  React.useEffect(() => {
    if (!matches.some((match) => match.id === selectedMatchId)) {
      setSelectedMatchId(matches[0]?.id);
    }
  }, [matches, selectedMatchId]);

  const selectedMatch = matches.find((match) => match.id === selectedMatchId);

  return (
    <div className={cn('flex min-h-0 flex-col gap-4', className)}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          {renderDialogLabels ? (
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Review possible patient matches"
                id="RqCrtSze+Y"
                description="Title for reviewing suggested patient match details"
              />
            </DialogTitle>
          ) : (
            <TypographyH5 className="text-text-primary">
              <FormattedMessage
                defaultMessage="Review possible patient matches"
                id="RqCrtSze+Y"
                description="Title for reviewing suggested patient match details"
              />
            </TypographyH5>
          )}
          {renderDialogLabels ? (
            <DialogDescription className="mt-1.5">
              <FormattedMessage
                defaultMessage="We couldn't confidently link this session, but we found some matches for you! Please select the correct patient for this session."
                id="FyxtAVZwsv"
                description="Description for the suggested patient match review dialog"
              />
            </DialogDescription>
          ) : (
            <TypographyP2 className="mt-1.5 text-text-secondary">
              <FormattedMessage
                defaultMessage="We couldn't confidently link this session, but we found some matches for you! Please select the correct patient for this session."
                id="FyxtAVZwsv"
                description="Description for the suggested patient match review dialog"
              />
            </TypographyP2>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          iconOnly
          aria-label={intl.formatMessage({
            defaultMessage: 'Close match review',
            id: 'fcgIxndvar',
            description: 'Accessible label for closing the suggested match review',
          })}
          onClick={onDismiss}
          className="-me-2 -mt-2 shrink-0"
        >
          <X />
        </Button>
      </div>

      {matches.length > 0 ? (
        <div className="min-h-0 overflow-hidden rounded-md border border-border">
          <div className="grid grid-cols-[2rem_minmax(9rem,11rem)_minmax(12rem,1fr)] border-b border-border bg-surface p-2">
            <div aria-hidden />
            <TypographyP2Bold className="text-text-primary">
              <FormattedMessage
                defaultMessage="Patient"
                id="+mDUrHTHkD"
                description="Patient column heading in the suggested match review dialog"
              />
            </TypographyP2Bold>
            <TypographyP2Bold className="text-text-primary">
              <FormattedMessage
                defaultMessage="Medical History"
                id="RbRyfy0Tp7"
                description="Medical history column heading in the suggested match review dialog"
              />
            </TypographyP2Bold>
          </div>
          <RadioGroup
            value={selectedMatchId}
            onValueChange={setSelectedMatchId}
            className="max-h-[360px] gap-0 overflow-y-auto"
          >
            {matches.map((match) => (
              <SuggestedMatchReviewRow
                key={match.id}
                match={match}
                selected={match.id === selectedMatchId}
              />
            ))}
          </RadioGroup>
        </div>
      ) : (
        <TypographyP2 className="rounded-md border border-border p-4 text-text-secondary">
          <FormattedMessage
            defaultMessage="No suggested matches available right now."
            id="BrTVdjcFhL"
            description="Empty state for suggested patient match review"
          />
        </TypographyP2>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" size="md" onClick={onDismiss}>
          <FormattedMessage
            defaultMessage="I'll do it later"
            id="c4g2mn7dZz"
            description="Button label for dismissing suggested patient match review without linking"
          />
        </Button>
        <Button
          type="button"
          size="md"
          disabled={!selectedMatch}
          onClick={() => {
            if (selectedMatch) {
              onConfirmMatch(selectedMatch);
            }
          }}
        >
          <FormattedMessage
            defaultMessage="Confirm"
            id="inDu8nuCFc"
            description="Button label for confirming the selected suggested patient match"
          />
        </Button>
      </div>
    </div>
  );
}

function SuggestedMatchReviewRow({
  match,
  selected,
}: {
  match: SuggestedPatientMatch;
  selected: boolean;
}) {
  const rowId = `suggested-match-review-${match.id}`;

  return (
    <label
      htmlFor={rowId}
      className={cn(
        'grid cursor-pointer grid-cols-[2rem_minmax(9rem,11rem)_minmax(12rem,1fr)] gap-x-0 border-b border-border px-2 py-3 last:border-b-0 hover:bg-surface-hover',
        selected && 'bg-surface-hover'
      )}
    >
      <div className="pt-1">
        <RadioGroupItem id={rowId} value={match.id} className="size-3.5" />
      </div>
      <div className="min-w-0 pe-4">
        <TypographyP2 className="truncate text-text-primary">
          <UserData>{match.fullName}</UserData>
        </TypographyP2>
        <SuggestedMatchReviewMetadata match={match} />
      </div>
      <TypographyCaption className="min-w-0 text-text-secondary">
        {match.details ?? null}
      </TypographyCaption>
    </label>
  );
}

function SuggestedMatchReviewMetadata({ match }: { match: SuggestedPatientMatch }) {
  const intl = useIntl();
  const birthDate = match.birthDate ? formatDisplayDate(match.birthDate, intl) : null;
  const nextAppointmentDate = match.nextAppointmentDate
    ? formatDisplayDate(match.nextAppointmentDate, intl)
    : null;

  if (!match.genderLabel && !birthDate && !nextAppointmentDate) return null;

  return (
    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
      {match.genderLabel && (
        <TypographyCaption className="text-text-secondary">
          {match.genderLabel}
        </TypographyCaption>
      )}
      {match.genderLabel && birthDate && <MetadataDot />}
      {birthDate && (
        <TypographyCaption className="text-text-secondary">{birthDate}</TypographyCaption>
      )}
      {nextAppointmentDate && (
        <>
          <MetadataDot />
          <span className="inline-flex min-w-0 items-center gap-x-1">
            <Sprout className="size-4 shrink-0 text-validation-success" />
            <TypographyCaption className="truncate text-validation-info">
              <FormattedMessage
                defaultMessage="Next apt. {date}"
                id="8WEEpbxa9z"
                description="Short label for the next appointment date on a suggested patient match"
                values={{ date: nextAppointmentDate }}
              />
            </TypographyCaption>
          </span>
        </>
      )}
    </div>
  );
}

function MetadataDot() {
  return <span aria-hidden className="size-1 rounded-full bg-text-tertiary" />;
}
